import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, NumericTextBox } from '@progress/kendo-react-inputs';
import { produce } from 'immer';
import { useCallback, useEffect, useState } from 'react';
import { useBlocker } from 'react-router';
import { ExtractKeysExtend } from '../../../../../../../components/helpers/helpers';
import { SaveOutline } from '../../../../../../../icons/icons';
import { defaultAirportMonitorOptions } from '../../../../../../../monitoring/monitor-defaults';
import { CompanyThresholdsMode, MonitorAirportOptions } from '../../../../../../../types/thresholds';
import { LoadingIndicatorCircular } from '../../../../../../components/indicators/LoadingIndicatorCircular';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store-hooks';
import { updateMonitorSettings } from '../../../../../../state/slices/global-slice';
import { store } from '../../../../../../state/store';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../../context/SettingsNavContext';
import { ThresholdHeaderBar } from '../common/ThresholdHeaderBar';

type Tab = 'company' | 'user';
type Data = {
    user: MonitorAirportOptions;
    company: MonitorAirportOptions;
};

export default function SettingsAirportThresholdOptions() {
    useSettingsPageWidth('700px');
    const [busy, setBusy] = useState(false);
    const [edited, setEdited] = useState(false);
    const [data, setData] = useState<Data>();
    const [mode, setMode] = useState(CompanyThresholdsMode.notUsed);
    const [tab, setTab] = useState<Tab>('user');
    const canEditCompanyThresholds = useAppSelector((x) => x.user.rights.canEditCompanyThresholds);
    const dispatch = useAppDispatch();
    const blocker = useBlocker(edited);
    const selectedData = data?.[tab];
    useEffect(() => {
        const state = store.getState().global.monitorSettings;
        setData({
            user: state.user.airport.options,
            company: state.company.airport.options
        });
        setMode(state.company.mode);
    }, []);


    const updateMode = (mode: CompanyThresholdsMode) => {
        setMode(mode);
        setEdited(true);
    };

    const handleSave = async () => {
        // Do we need try catch here? don't think so, async thunk already handles exceptions
        try {
            setBusy(true);
            const result = await dispatch(
                updateMonitorSettings({
                    user: {
                        airport: { options: data?.user }
                    },
                    company: canEditCompanyThresholds ? { airport: { options: data?.company }, companyMode: mode } : undefined
                })
            ).unwrap();
            if (result) {
                setEdited(false);
            }
            return result;
        } catch {
            // Already handled
        } finally {
            setBusy(false);
        }

        return false;
    };

    const handleSaveConfirm = async () => {
        const result = await handleSave();
        if (result) {
            if (blocker.state === 'blocked') {
                blocker.proceed();
            }
        }
    };

    const updateData = useCallback(
        (recipe: (draft: MonitorAirportOptions) => void) => {
            setEdited(true);
            setData((p) =>
                produce(p, (d) => {
                    recipe(d![tab]);
                })
            );
        },
        [tab]
    );

    const handleRestoreDefaults = useCallback(() => {
        setEdited(true);
        setData((p) => ({
            ...p!,
            [tab]: {
                // Set everything to null to force defaults
                tafWindowHours: null,
                airportMonitorStatusInclude: {
                    originThresholds: null,
                    destinationThresholds: null,
                    alternateThresholds: null
                }
            } satisfies MonitorAirportOptions
        }));
    }, [tab]);

    const isUserAndMinimum = tab === 'user' && mode === CompanyThresholdsMode.minimum;
    const companySet =
        tab === 'user' && (mode === CompanyThresholdsMode.minimum || mode === CompanyThresholdsMode.template) ? data?.company : null;
    const tafMergeFunction =
        tab === 'user'
            ? mode === CompanyThresholdsMode.minimum
                ? tafMergeMinimum
                : mode === CompanyThresholdsMode.template
                  ? tafMergeTemplate
                  : tafEmptyMerge
            : tafEmptyMerge;

    const minTafValue = isUserAndMinimum ? (companySet?.tafWindowHours ?? defaultAirportMonitorOptions.tafWindowHours) : 0;
    const readonly = tab === 'company' && !canEditCompanyThresholds;

    const originChecked = mergeBooleans(tab, 'originThresholds', selectedData, companySet, mode);
    const destinationChecked = mergeBooleans(tab, 'destinationThresholds', selectedData, companySet, mode);
    const altChecked = mergeBooleans(tab, 'alternateThresholds', selectedData, companySet, mode);

    return (
        <div className="pb-2 h-100">
            {blocker.state === 'blocked' && (
                <Dialog title="Airport thresholds" onClose={() => blocker.reset()}>
                    <div>Do you want to save pending changes?</div>
                    <DialogActionsBar>
                        <Button themeColor={'primary'} onClick={handleSaveConfirm} disabled={busy}>
                            Yes
                        </Button>
                        <Button fillMode={'outline'} onClick={() => blocker.proceed()} disabled={busy}>
                            No
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <SettingsNavTitle>
                <div className="d-flex justify-content-between align-items-center">
                    <div>Airport threshold options</div>
                    {busy && <LoadingIndicatorCircular />}
                    <Button svgIcon={SaveOutline} fillMode={'outline'} disabled={!edited} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </SettingsNavTitle>

            <div className="k-card max-h-100">
                <ThresholdHeaderBar
                    tab={tab}
                    companyMode={mode}
                    onChangeTab={setTab}
                    onChangeCompanyMode={updateMode}
                    hideClearAll
                    onRestoreDefaultClicked={handleRestoreDefaults}
                />

                {selectedData && (
                    <div className="p-3">
                        <div>
                            <span>Use only the first </span>
                            <NumericTextBox
                                width={125}
                                value={tafMergeFunction(
                                    selectedData.tafWindowHours,
                                    companySet?.tafWindowHours,
                                    defaultAirportMonitorOptions.tafWindowHours
                                )}
                                min={minTafValue}
                                max={36}
                                disabled={readonly}
                                onChange={(e) => updateData((x) => (x.tafWindowHours = e.value as number))}
                            />
                            <span> hours of TAF forecasts.</span>
                        </div>

                        <div className="pt-2">Overall monitor status for airport is based on:</div>

                        <Checkbox label="Airport thresholds" checked disabled />

                        <Checkbox
                            label="Origin thresholds"
                            checked={originChecked}
                            disabled={
                                readonly ||
                                (isUserAndMinimum &&
                                    (companySet?.airportMonitorStatusInclude.originThresholds ??
                                        defaultAirportMonitorOptions.airportMonitorStatusInclude.originThresholds) === true)
                            }
                            onChange={(e) => updateData((x) => (x.airportMonitorStatusInclude.originThresholds = e.value))}
                        />
                        <Checkbox
                            label="Destination thresholds"
                            checked={destinationChecked}
                            disabled={
                                readonly ||
                                (isUserAndMinimum &&
                                    (companySet?.airportMonitorStatusInclude.destinationThresholds ??
                                        defaultAirportMonitorOptions.airportMonitorStatusInclude.destinationThresholds) === true)
                            }
                            onChange={(e) => updateData((x) => (x.airportMonitorStatusInclude.destinationThresholds = e.value))}
                        />
                        <Checkbox
                            label="Destination alternate thresholds"
                            checked={altChecked}
                            disabled={
                                readonly ||
                                (isUserAndMinimum &&
                                    (companySet?.airportMonitorStatusInclude.alternateThresholds ??
                                        defaultAirportMonitorOptions.airportMonitorStatusInclude.alternateThresholds) === true)
                            }
                            onChange={(e) => updateData((x) => (x.airportMonitorStatusInclude.alternateThresholds = e.value))}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function tafMergeMinimum(a: number | null, b: number | null | undefined, defaultValue: number): number {
    if (a == null && b == null) {
        return defaultValue;
    }

    if (a == null) {
        return b!;
    }

    if (b == null) {
        return a;
    }

    return Math.max(a, b);
}

function tafMergeTemplate(a: number | null, b: number | null | undefined, defaultValue: number): number {
    return a ?? b ?? defaultValue;
}

function tafEmptyMerge(a: number | null, b: number | null | undefined, defaultValue: number): number {
    return a ?? defaultValue;
}

function mergeBooleans(
    tab: Tab,
    key: ExtractKeysExtend<MonitorAirportOptions['airportMonitorStatusInclude'], boolean | null>,
    user: MonitorAirportOptions | null | undefined,
    company: MonitorAirportOptions | null | undefined,
    mode: CompanyThresholdsMode
): boolean {
    const a = user?.airportMonitorStatusInclude[key];
    const b = company?.airportMonitorStatusInclude[key];
    if (mode === CompanyThresholdsMode.notUsed || mode === CompanyThresholdsMode.specified) {
        return a ?? defaultAirportMonitorOptions.airportMonitorStatusInclude[key];
    } else if (mode === CompanyThresholdsMode.template) {
        if (tab === 'user') {
            return a ?? b ?? defaultAirportMonitorOptions.airportMonitorStatusInclude[key];
        }
        return a ?? defaultAirportMonitorOptions.airportMonitorStatusInclude[key];
    } else if (mode === CompanyThresholdsMode.minimum) {
        if (tab === 'user') {
            return a === true || (b ?? defaultAirportMonitorOptions.airportMonitorStatusInclude[key]);
        }
        return a ?? defaultAirportMonitorOptions.airportMonitorStatusInclude[key];
    }
    throw new Error('Unknown company mode');
}
